import { Home, User } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
// material
import { Avatar, Box, Button, Divider, MenuItem, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"

import { useAuth } from "../../components/auth/AuthProvider"
import { MIconButton } from "../../components/library/@material-extend"
import MenuPopover from "../../components/MenuPopover"
// components

// ----------------------------------------------------------------------

const MENU_OPTIONS = [
  { label: "Home", icon: Home, linkTo: "/" },
  { label: "Profile", icon: User, linkTo: "/profile" }
]

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)

  // Close popover when route changes
  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  const handleOpen = (event: React.MouseEvent) => {
    event.stopPropagation()
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
  }
  const handleMenuItemClick = (event: React.MouseEvent, path: string) => {
    event.preventDefault()
    event.stopPropagation()
    setOpen(false)
    navigate(path)
  }
  const handleLogoutButtonClick = () => {
    auth?.logOut()
  }

  return (
    <>
      <MIconButton
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          padding: 0,
          width: 44,
          height: 44,
          ...(open && {
            "&:before": {
              zIndex: 1,
              content: "''",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              position: "absolute",
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72)
            }
          })
        }}
      >
        <Avatar
          alt="My Avatar"
          src={auth?.user?.picture || "/static/mock-images/avatars/avatar_default.jpg"}
        />
      </MIconButton>
      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        disableScrollLock
        sx={{ width: 220 }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle1" noWrap>
            {auth?.user?.name || ""}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
            {auth?.user?.email || ""}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        {MENU_OPTIONS.map((option) => {
          const IconComponent = option.icon
          return (
            <MenuItem
              key={option.label}
              onClick={(event) => handleMenuItemClick(event, option.linkTo)}
              sx={{
                typography: "body2",
                py: 1,
                px: 2.5,
                cursor: "pointer",
                "&:hover": {
                  bgcolor: "action.hover"
                }
              }}
            >
              <Box
                component="span"
                sx={{
                  mr: 2,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <IconComponent size={24} aria-label={option.label} />
              </Box>
              {option.label}
            </MenuItem>
          )
        })}

        <Box sx={{ p: 2, pt: 1.5 }}>
          <Button fullWidth color="inherit" variant="outlined" onClick={handleLogoutButtonClick}>
            Logout
          </Button>
        </Box>
      </MenuPopover>
    </>
  )
}
